import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';
import { cn } from '@/lib/cn';

const CREATE_VALUE = '__create__';

export type CashbookCategoryOption = {
  value: string;
  label: string;
};

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  categories: CashbookCategoryOption[];
  disabled?: boolean;
  className?: string;
  allowCreate?: boolean;
  placeholder?: string;
  'aria-label'?: string;
};

export function CashbookCategorySelect({
  value,
  onValueChange,
  categories,
  disabled,
  className,
  allowCreate = false,
  placeholder,
  'aria-label': ariaLabel
}: Props) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const inSavedList = categories.some(
    (c) => c.value.toLowerCase() === value.trim().toLowerCase()
  );
  const showCreateInput = allowCreate && (creating || categories.length === 0);

  const options = useMemo((): SelectOption[] => {
    const base: SelectOption[] = categories.map((c) => ({
      value: c.value,
      label: c.label
    }));
    if (allowCreate) {
      base.push({
        value: CREATE_VALUE,
        label: t('cashbook.addNewCategory')
      });
    }
    return base;
  }, [categories, allowCreate, t]);

  if (showCreateInput) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Input
          className="w-full"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          placeholder={t('cashbook.newCategoryPlaceholder')}
          aria-label={ariaLabel ?? t('cashbook.category')}
        />
        {categories.length > 0 ? (
          <button
            type="button"
            className="text-left text-xs text-[var(--fv-primary)] hover:underline disabled:opacity-50"
            disabled={disabled}
            onClick={() => {
              setCreating(false);
              if (value && !inSavedList) onValueChange('');
            }}
          >
            {t('cashbook.selectExistingCategory')}
          </button>
        ) : null}
      </div>
    );
  }

  const selectValue = inSavedList ? value : '';

  return (
    <Select
      className={className}
      value={selectValue}
      onValueChange={(next) => {
        if (next === CREATE_VALUE) {
          setCreating(true);
          onValueChange('');
          return;
        }
        setCreating(false);
        onValueChange(next);
      }}
      options={options}
      disabled={disabled}
      placeholder={placeholder ?? t('cashbook.selectCategory')}
      aria-label={ariaLabel ?? t('cashbook.category')}
    />
  );
}
